import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError, timeout } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PagedResult } from '../models/paged-result.model';
import { ServiceOrderListItemDto } from '../responses/service-order-list-item.dto';
import { AuthService } from './auth.service';
import { InspectionStepResult } from '../../../features/new-os/steps/step-inspection/step-inspection';
import { ApprovalOSRequest } from '../request/approval-os.request';

export interface ServiceOrderDetailResponse {
    documentoId: number;
    numero: number;
    status: string | null;
    dtHrEmissao: string;
    vehicle: {
        id: number;
        configVehicleId?: number | null;
        plate: string;
        model: string | null;
        brand: string | null;
        yearModel: number | null;
        yearManufacture: number | null;
        chassis: string | null;
        color: string | null;
        fuel: number | null;
    } | null;
    client: {
        id: number;
        nome: string;
        cpfCnpj: string | null;
        email: string | null;
    } | null;
    responsibles: {
        seller: { id: number; nome: string } | null;
        mechanic: { id: number; nome: string } | null;
    };
    vistoria: {
        kmEntrada: number | null;
        tanque: number | null;
        observacaoOficina: string | null;
        problema: string | null;
        photos: Array<{ id: number; slot: string | null; mimeType: string | null; dataCriacao: string }>;
        avarias: Array<{ fotoVista: string; coordenadaX: number; coordenadaY: number }>;
    } | null;
    itens: Array<{
        id: number;
        produtoId: number;
        descricao: string;
        codigoTopCar: string | null;
        grupo: string | null;
        subGrupo: string | null;
        quantidade: number;
        precoUnitario: number;
        precoTotal: number;
        tipoProduto: string;
    }>;
}

export interface CriarRascunhoRequest {
    veiculoId: number;
    pessoaId: number;
}

export interface CriarRascunhoResponse {
    documentoId: number;
    numero: number;
}

export interface SalvarResponsaveisRascunhoRequest {
    sellerId: number;
    mechanicId?: number;
}

export interface SalvarItensRascunhoRequest {
    itens: Array<{
        produtoId: number;
        quantidade: number;
        precoUnitario: number;
    }>;
}

export interface AlterarStatusServiceOrderRequest {
    status: string;
}

@Injectable({
    providedIn: 'root'
})
export class ServiceOrderApiService {
    private readonly apiUrl = environment.urlApi ? `${environment.urlApi}/ServiceOrder` : '/ats-resulth-car-api-mobile/ServiceOrder';
    private readonly apiLastWeekUrl = environment.urlApi ? `${environment.urlApi}/ServiceOrder/last-week` : '/ats-resulth-car-api-mobile/ServiceOrder/last-week';
    private readonly apiRascunhosUrl = environment.urlApi ? `${environment.urlApi}/ServiceOrder/rascunhos` : '/ats-resulth-car-api-mobile/ServiceOrder/rascunhos';
    private readonly apiPendingUrl = environment.urlApi ? `${environment.urlApi}/ServiceOrder/pending` : '/ats-resulth-car-api-mobile/ServiceOrder/pending';
    private readonly apiSearchUrl = environment.urlApi ? `${environment.urlApi}/ServiceOrder/search` : '/ats-resulth-car-api-mobile/ServiceOrder/search';
    private readonly apiApprovalOSUrl = environment.urlApi ? `${environment.urlApi}/ServiceOrder/approval-os` : '/ats-resulth-car-api-mobile/ServiceOrder/approval-os';

    constructor(
        private http: HttpClient,
        private authService: AuthService
    ) { }

    criarRascunho(veiculoId: number, pessoaId: number): Observable<CriarRascunhoResponse> {
        const body: CriarRascunhoRequest = { veiculoId, pessoaId };
        return this.http.post<unknown>(
            `${this.apiUrl}/rascunho`,
            body,
            this.addHeaderToken()
        ).pipe(
            timeout(15000),
            map((response) => this.normalizeCriarRascunhoResponse(response))
        );
    }

    salvarVistoriaRascunho(documentoId: number, data: InspectionStepResult): Observable<void> {
        const form = new FormData();

        // ── Scalar fields ─────────────────────────────────────────────────────
        if (data.odometer != null) form.append('kmEntrada', String(data.odometer));

        if (data.fuelLevel != null) {
            const tankMap: Record<string, string> = {
                'RES': '0', '1/4': '1', '1/2': '2', '3/4': '3', 'CHEIO': '4'
            };
            form.append('tanque', tankMap[data.fuelLevel] ?? '0');
        }

        if (data.observations) form.append('observacaoOficina', data.observations);
        if (data.problems)     form.append('problema',          data.problems);

        // ── Photo files ───────────────────────────────────────────────────────
        const fieldMap: Record<string, string> = {
            Frontal:   'fotoFrontal',
            Traseira:  'fotoTraseira',
            Esquerda:  'fotoEsquerda',
            Direita:   'fotoDireita',
            Hodometro: 'fotoHodometro',
        };

        for (const [slot, fieldName] of Object.entries(fieldMap)) {
            const file = (data.photoFiles as Record<string, File | undefined>)[slot];
            if (file) form.append(fieldName, file, 'vistoria.jpg');
        }

        // ── Avaria markers ────────────────────────────────────────────────────
        // Map frontend InspectionSide values to the DB CHECK-constrained strings.
        const sideMap: Record<string, string> = {
            'Frente':   'Frente',
            'Traseira': 'Tras',
            'Esquerdo': 'Lateral Esquerda',
            'Direito':  'Lateral Direita',
        };

        const avarias = (data.markers ?? []).map(m => ({
            fotoVista:   sideMap[m.side] ?? m.side,
            coordenadaX: m.x,
            coordenadaY: m.y,
            largura:     0,  // point marker — no selection region
            altura:      0,
            observacao:  null as string | null
        }));

        // Always send the field (even as '[]') so the backend performs a full
        // replace and clears previously saved markers when all are removed.
        form.append('avarias', JSON.stringify(avarias));

        const options = { ...this.addHeaderToken(), responseType: 'text' as const };
        return this.http.put(
            `${this.apiUrl}/rascunho/${documentoId}/vistoria`,
            form,
            options
        ).pipe(
            timeout(60000), // allow extra time for photo uploads
            map(() => undefined)
        );
    }

    salvarResponsaveisRascunho(documentoId: number, sellerId: number, mechanicId?: number): Observable<void> {
        const body: SalvarResponsaveisRascunhoRequest = { sellerId, mechanicId };
        const options = { ...this.addHeaderToken(), responseType: 'text' as const };
        return this.http.put(
            `${this.apiUrl}/rascunho/${documentoId}/responsaveis`,
            body,
            options
        ).pipe(
            timeout(15000),
            map(() => undefined)
        );
    }

    salvarItensRascunho(documentoId: number, itens: SalvarItensRascunhoRequest['itens']): Observable<void> {
        const body: SalvarItensRascunhoRequest = { itens };
        const options = { ...this.addHeaderToken(), responseType: 'text' as const };
        return this.http.put(
            `${this.apiUrl}/rascunho/${documentoId}/itens`,
            body,
            options
        ).pipe(
            timeout(15000),
            map(() => undefined)
        );
    }

    abrirRascunho(documentoId: number): Observable<void> {
        return this.http.put(
            `${this.apiUrl}/rascunho/${documentoId}/abrir`,
            {},
            this.addHeaderToken()
        ).pipe(
            timeout(15000),
            map((response) => {
                this.ensureOpenOsSucceeded(response);
                return undefined;
            }),
            catchError((error: unknown) => throwError(() => this.normalizeOpenOsError(error)))
        );
    }

    alterarStatus(documentoId: number, status: string): Observable<void> {
        const body: AlterarStatusServiceOrderRequest = { status };
        const options = { ...this.addHeaderToken(), responseType: 'text' as const };
        return this.http.put(
            `${this.apiUrl}/${documentoId}/status`,
            body,
            options
        ).pipe(
            timeout(15000),
            map(() => undefined)
        );
    }

    recusarRascunho(documentoId: number): Observable<void> {
        const options = { ...this.addHeaderToken(), responseType: 'text' as const };
        return this.http.put(
            `${this.apiUrl}/rascunho/${documentoId}/recusar`,
            {},
            options
        ).pipe(
            timeout(15000),
            map(() => undefined)
        );
    }

    approvalOS(request: ApprovalOSRequest): Observable<void> {
        const options = { ...this.addHeaderToken(), responseType: 'text' as const };
        return this.http.put(
            `${this.apiApprovalOSUrl}`,
            request,
            options
        ).pipe(
            timeout(15000),
            map(() => undefined)
        );
    }

    getGedPhoto(gedId: number): Observable<Blob> {
        return this.http.get(
            `${this.apiUrl}/ged/${gedId}`,
            { ...this.addHeaderToken(), responseType: 'blob' }
        ).pipe(timeout(30000));
    }

    getById(documentoId: number): Observable<ServiceOrderDetailResponse> {
        return this.http.get<unknown>(
            `${this.apiUrl}/${documentoId}`,
            this.addHeaderToken()
        ).pipe(
            timeout(15000),
            map((response) => this.normalizeDetailResponse(response))
        );
    }

    list(page = 1, pendingOnly?: boolean, pageSize = 20): Observable<PagedResult<ServiceOrderListItemDto>> {
        const params = this.buildParams(page, pendingOnly, pageSize, undefined, undefined);

        return this.http.get<unknown>(this.apiUrl, { params, ...this.addHeaderToken() }).pipe(
            timeout(15000),
            map((response) => this.normalizePagedResult(response, page, pageSize))
        );
    }

    listPending(page = 1, pendingOnly?: boolean, pageSize = 20): Observable<PagedResult<ServiceOrderListItemDto>> {
        const params = this.buildParams(page, pendingOnly, pageSize, undefined, undefined);

        return this.http.get<unknown>(this.apiPendingUrl, { params, ...this.addHeaderToken() }).pipe(
            timeout(15000),
            map((response) => this.normalizePagedResult(response, page, pageSize))
        );
    }

    listLastWeek(page = 1, pendingOnly?: boolean, pageSize = 20): Observable<PagedResult<ServiceOrderListItemDto>> {
        const params = this.buildParams(page, pendingOnly, pageSize, undefined, undefined);

        return this.http.get<unknown>(this.apiLastWeekUrl, { params, ...this.addHeaderToken() }).pipe(
            timeout(15000),
            map((response) => this.normalizePagedResult(response, page, pageSize))
        );
    }

    listRascunhos(page = 1, pendingOnly?: boolean, pageSize = 20): Observable<PagedResult<ServiceOrderListItemDto>> {
        const params = this.buildParams(page, pendingOnly, pageSize, undefined, undefined);
        return this.http.get<unknown>(this.apiRascunhosUrl, { params, ...this.addHeaderToken() }).pipe(
            timeout(15000),
            map((response) => this.normalizePagedResult(response, page, pageSize))
        );
    }

    search(search: string, page = 1, pageSize = 20, status = '', pendingOnly?: boolean): Observable<PagedResult<ServiceOrderListItemDto>> {
        const params = this.buildParams(page, pendingOnly, pageSize, search, status);

        return this.http.get<unknown>(this.apiSearchUrl, { params, ...this.addHeaderToken() }).pipe(
            timeout(15000),
            map((response) => this.normalizePagedResult(response, page, pageSize))
        );
    }

    private buildParams(page: number, pendingOnly?: boolean, pageSize = 20, search?: string, status?: string): HttpParams {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());

        const normalizedSearch = (search || '').trim();
        if (normalizedSearch) {
            params = params.set('search', normalizedSearch);
        }

        const normalizedStatus = (status || '').trim();
        if (normalizedStatus) {
            params = params.set('status', normalizedStatus);
        }

        if (pendingOnly) {
            params = params.set('pendingOnly', 'true');
        }

        return params;
    }

    private addHeaderToken(): { headers: HttpHeaders } {
        const token = this.authService.getToken();
        return {
            headers: new HttpHeaders({
                Authorization: `Bearer ${token}`
            })
        };
    }

    private normalizePagedResult(response: unknown, page: number, pageSize: number, pendingOnly?: boolean): PagedResult<ServiceOrderListItemDto> {
        const unwrapped = this.unwrapResponse(response);

        if (Array.isArray(unwrapped)) {
            return {
                items: unwrapped.map((item) => this.normalizeItem(item)),
                page,
                pageSize,
                totalCount: unwrapped.length,
                totalPages: 1,
                hasPreviousPage: false,
                hasNextPage: false
            };
        }

        const payload = (unwrapped ?? {}) as Record<string, unknown>;
        const itemsValue =
            payload['items'] ??
            payload['Items'] ??
            payload['data'] ??
            payload['Data'] ??
            payload['result'] ??
            payload['Result'] ??
            [];
        const rawItems = this.toArray(itemsValue);

        return {
            items: rawItems.map((item) => this.normalizeItem(item)),
            page: this.numberOrDefault(payload['page'] ?? payload['Page'], page),
            pageSize: this.numberOrDefault(payload['pageSize'] ?? payload['PageSize'], pageSize),
            totalCount: this.numberOrDefault(payload['totalCount'] ?? payload['TotalCount'], rawItems.length),
            totalPages: this.numberOrDefault(payload['totalPages'] ?? payload['TotalPages'], 1),
            hasPreviousPage: this.booleanOrDefault(payload['hasPreviousPage'] ?? payload['HasPreviousPage'], false),
            hasNextPage: this.booleanOrDefault(payload['hasNextPage'] ?? payload['HasNextPage'], false)
        };
    }

    private normalizeItem(raw: unknown): ServiceOrderListItemDto {
        const item = (raw ?? {}) as Record<string, unknown>;
        return {
            documentoId: this.numberOrDefault(item['documentoId'] ?? item['DocumentoId'], 0),
            numero: this.numberOrDefault(item['numero'] ?? item['Numero'], 0),
            dtHrEmissao: String(item['dtHrEmissao'] ?? item['DtHrEmissao'] ?? ''),
            plate: this.stringOrNull(item['plate'] ?? item['Plate']),
            status: this.stringOrNull(item['status'] ?? item['Status']),
            marca: this.stringOrNull(item['marca'] ?? item['Marca']),
            vehicle: this.stringOrNull(item['vehicle'] ?? item['Vehicle']),
            vehicleYear: this.numberOrNull(item['vehicleYear'] ?? item['VehicleYear']),
            client: this.stringOrNull(item['client'] ?? item['Client']),
            mechanicName: this.stringOrNull(item['mechanicName'] ?? item['MechanicName']),
            mechanicCodigoTopCar: this.numberOrNull(item['mechanicCodigoTopCar'] ?? item['MechanicCodigoTopCar']),
            sellerName: this.stringOrNull(item['sellerName'] ?? item['SellerName']),
            sellerCodigoTopCar: this.numberOrNull(item['sellerCodigoTopCar'] ?? item['SellerCodigoTopCar']),
            oSItensLists: this.toArray(item['oSItensLists'] ?? item['OSItensLists'] ?? []).map((it) => {
                const i = (it ?? {}) as Record<string, unknown>;
                return {
                    documentoItemId: this.numberOrNull(i['documentoItemID'] ?? i['DocumentoItemId']),
                    codigo: this.stringOrNull(i['codigo'] ?? i['Codigo']),
                    descricao: this.stringOrNull(i['descricao'] ?? i['Descricao']),
                    quantidade: this.stringOrNull(i['quantidade'] ?? i['Quantidade']),
                    valorOriginal: this.stringOrNull(i['valorOriginal'] ?? i['ValorOriginal']),
                    desconto: this.stringOrNull(i['desconto'] ?? i['Desconto']),
                    total: this.stringOrNull(i['total'] ?? i['Total']),
                    flagAprovado: (i['flagAprovado'] ?? i['FlagAprovado']) != null ? Boolean(i['flagAprovado'] ?? i['FlagAprovado']) : null,
                    flagPendente: (i['flagPendente'] ?? i['FlagPendente']) != null ? Boolean(i['flagPendente'] ?? i['FlagPendente']) : null
                };
            })
        };
    }

    private normalizeCriarRascunhoResponse(response: unknown): CriarRascunhoResponse {
        const unwrapped = this.unwrapResponse(response);
        const data = (unwrapped ?? {}) as Record<string, unknown>;
        return {
            documentoId: this.numberOrDefault(data['documentoId'] ?? data['DocumentoId'], 0),
            numero: this.numberOrDefault(data['numero'] ?? data['Numero'], 0)
        };
    }

    private normalizeDetailResponse(response: unknown): ServiceOrderDetailResponse {
        const unwrapped = this.unwrapResponse(response);
        const d = (unwrapped ?? {}) as Record<string, unknown>;

        const rawVehicle = (d['vehicle'] ?? d['Vehicle']) as Record<string, unknown> | null | undefined;
        const rawClient = (d['client'] ?? d['Client']) as Record<string, unknown> | null | undefined;
        const rawResp = (d['responsibles'] ?? d['Responsibles'] ?? {}) as Record<string, unknown>;
        const rawSeller = (rawResp['seller'] ?? rawResp['Seller']) as Record<string, unknown> | null | undefined;
        const rawMechanic = (rawResp['mechanic'] ?? rawResp['Mechanic']) as Record<string, unknown> | null | undefined;
        const rawVistoria = (d['vistoria'] ?? d['Vistoria']) as Record<string, unknown> | null | undefined;

        return {
            documentoId: this.numberOrDefault(d['documentoId'] ?? d['DocumentoId'], 0),
            numero: this.numberOrDefault(d['numero'] ?? d['Numero'], 0),
            status: this.stringOrNull(d['status'] ?? d['Status']),
            dtHrEmissao: String(d['dtHrEmissao'] ?? d['DtHrEmissao'] ?? ''),
            vehicle: rawVehicle ? {
                id: this.numberOrDefault(rawVehicle['id'] ?? rawVehicle['Id'], 0),
                configVehicleId: this.numberOrNull(rawVehicle['configVehicleId'] ?? rawVehicle['ConfigVehicleId']),
                plate: String(rawVehicle['plate'] ?? rawVehicle['Plate'] ?? ''),
                model: this.stringOrNull(rawVehicle['model'] ?? rawVehicle['Model']),
                brand: this.stringOrNull(rawVehicle['brand'] ?? rawVehicle['Brand']),
                yearModel: this.numberOrNull(rawVehicle['yearModel'] ?? rawVehicle['YearModel']),
                yearManufacture: this.numberOrNull(rawVehicle['yearManufacture'] ?? rawVehicle['YearManufacture']),
                chassis: this.stringOrNull(rawVehicle['chassis'] ?? rawVehicle['Chassis']),
                color: this.stringOrNull(rawVehicle['color'] ?? rawVehicle['Color']),
                fuel: this.numberOrNull(rawVehicle['fuel'] ?? rawVehicle['Fuel']),
            } : null,
            client: rawClient ? {
                id: this.numberOrDefault(rawClient['id'] ?? rawClient['Id'], 0),
                nome: String(rawClient['nome'] ?? rawClient['Nome'] ?? ''),
                cpfCnpj: this.stringOrNull(rawClient['cpfCnpj'] ?? rawClient['CpfCnpj']),
                email: this.stringOrNull(rawClient['email'] ?? rawClient['Email']),
            } : null,
            responsibles: {
                seller: rawSeller ? {
                    id: this.numberOrDefault(rawSeller['id'] ?? rawSeller['Id'], 0),
                    nome: String(rawSeller['nome'] ?? rawSeller['Nome'] ?? ''),
                } : null,
                mechanic: rawMechanic ? {
                    id: this.numberOrDefault(rawMechanic['id'] ?? rawMechanic['Id'], 0),
                    nome: String(rawMechanic['nome'] ?? rawMechanic['Nome'] ?? ''),
                } : null,
            },
            vistoria: rawVistoria ? {
                kmEntrada: this.numberOrNull(rawVistoria['kmEntrada'] ?? rawVistoria['KmEntrada']),
                tanque: this.numberOrNull(rawVistoria['tanque'] ?? rawVistoria['Tanque']),
                observacaoOficina: this.stringOrNull(rawVistoria['observacaoOficina'] ?? rawVistoria['ObservacaoOficina']),
                problema: this.stringOrNull(rawVistoria['problema'] ?? rawVistoria['Problema']),
                photos: this.toArray(rawVistoria['photos'] ?? rawVistoria['Photos']).map((p) => {
                    const photo = (p ?? {}) as Record<string, unknown>;
                    return {
                        id: this.numberOrDefault(photo['id'] ?? photo['Id'], 0),
                        slot: this.stringOrNull(photo['slot'] ?? photo['Slot']),
                        mimeType: this.stringOrNull(photo['mimeType'] ?? photo['MimeType']),
                        dataCriacao: String(photo['dataCriacao'] ?? photo['DataCriacao'] ?? ''),
                    };
                }),
                avarias: this.toArray(rawVistoria['avarias'] ?? rawVistoria['Avarias']).map((a) => {
                    const av = (a ?? {}) as Record<string, unknown>;
                    return {
                        fotoVista: String(av['fotoVista'] ?? av['FotoVista'] ?? ''),
                        coordenadaX: this.numberOrDefault(av['coordenadaX'] ?? av['CoordenadaX'], 0),
                        coordenadaY: this.numberOrDefault(av['coordenadaY'] ?? av['CoordenadaY'], 0),
                    };
                }),
            } : null,
            itens: this.toArray(d['itens'] ?? d['Itens']).map((i) => {
                const item = (i ?? {}) as Record<string, unknown>;
                return {
                    id: this.numberOrDefault(item['id'] ?? item['Id'], 0),
                    produtoId: this.numberOrDefault(item['produtoId'] ?? item['ProdutoId'], 0),
                    descricao: String(item['descricao'] ?? item['Descricao'] ?? ''),
                    codigoTopCar: this.stringOrNull(item['codigoTopCar'] ?? item['CodigoTopCar']),
                    grupo: this.stringOrNull(item['grupo'] ?? item['Grupo']),
                    subGrupo: this.stringOrNull(item['subGrupo'] ?? item['SubGrupo']),
                    quantidade: this.numberOrDefault(item['quantidade'] ?? item['Quantidade'], 0),
                    precoUnitario: this.numberOrDefault(item['precoUnitario'] ?? item['PrecoUnitario'], 0),
                    precoTotal: this.numberOrDefault(item['precoTotal'] ?? item['PrecoTotal'], 0),
                    tipoProduto: String(item['tipoProduto'] ?? item['TipoProduto'] ?? ''),
                };
            }),
        };
    }

    private unwrapResponse(response: unknown): unknown {
        if (typeof response === 'string') {
            try {
                return JSON.parse(response);
            } catch {
                return response;
            }
        }

        if (!response || typeof response !== 'object' || Array.isArray(response)) {
            return response;
        }

        const payload = response as Record<string, unknown>;
        return payload['data'] ?? payload['Data'] ?? payload['result'] ?? payload['Result'] ?? response;
    }

    private normalizeOpenOsError(error: unknown): Error {
        if (error instanceof Error && error.message.trim()) {
            return error;
        }

        if (error instanceof HttpErrorResponse) {
            const message = this.extractErrorMessage(error.error);
            if (message) {
                return new Error(message);
            }
        }

        return new Error('Não foi possível abrir a OS. Tente novamente.');
    }

    private ensureOpenOsSucceeded(response: unknown): void {
        const unwrapped = this.unwrapResponse(response);
        if (!unwrapped || typeof unwrapped !== 'object' || Array.isArray(unwrapped)) {
            return;
        }

        const payload = unwrapped as Record<string, unknown>;
        const success = payload['success'] ?? payload['Success'];
        if (typeof success === 'boolean' && !success) {
            const message = this.extractErrorMessage(payload);
            throw new Error(message || 'Não foi possível abrir a OS. Tente novamente.');
        }
    }

    private extractErrorMessage(errorBody: unknown): string {
        if (typeof errorBody === 'string') {
            const raw = errorBody.trim();
            if (!raw) {
                return '';
            }

            try {
                return this.extractErrorMessage(JSON.parse(raw)) || raw;
            } catch {
                return raw;
            }
        }

        if (errorBody && typeof errorBody === 'object') {
            const body = errorBody as Record<string, unknown>;
            const message = body['message'] ?? body['Message'] ?? body['title'] ?? body['Title'] ?? body['error'] ?? body['Error'];
            return typeof message === 'string' ? message.trim() : '';
        }

        return '';
    }

    private toArray(value: unknown): unknown[] {
        if (Array.isArray(value)) {
            return value;
        }

        if (value && typeof value === 'object' && '$values' in (value as Record<string, unknown>)) {
            const values = (value as Record<string, unknown>)['$values'];
            return Array.isArray(values) ? values : [];
        }

        return [];
    }

    private numberOrDefault(value: unknown, fallback: number): number {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    }

    private numberOrNull(value: unknown): number | null {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private stringOrNull(value: unknown): string | null {
        return typeof value === 'string' ? value : null;
    }

    private booleanOrDefault(value: unknown, fallback: boolean): boolean {
        return typeof value === 'boolean' ? value : fallback;
    }
}
